/** @format */
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { ModulePlanState, PlanValidationResult, PlanValidationTs, ValidatablePlan } from 'common-typescript';
import {
    DegreeProgramme,
    DegreeProgrammeAttainment,
    DegreeProgrammeAttainmentWorkflow,
    DPSMBase,
    Module,
    ModuleAttainment,
    ModuleAttainmentWorkflow,
    OtmId,
    StudyModule,
    StudyRight,
    UniversitySettings,
    Workflow,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, combineLatestWith, merge, Observable, of, shareReplay, Subject, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from 'sis-common';
import {
    AppErrorHandler,
    isDegreeProgrammeAttainment,
    isModuleAttainment,
    ModuleEntityService,
    STUDENT_WORKFLOW_STATE,
    UniversityService,
    WorkflowEntityService,
} from 'sis-components';
import { AttainmentStudentService } from '../../../service/attainment-student.service';
import { DegreeProgrammeAttainmentApplicationService } from '../../../service/degree-programme-attainment-application.service';
import { PreviewModeService } from '../../../utils/preview-mode.service';
import { ModuleInfoService } from '../module-info.service';
import { studentModuleInfoVersionChangeWarningOpener } from './student-module-info-version-change-warning/student-module-info-version-change-warning.component';

export interface StudentModuleInfoModalValues {
    module: DPSMBase;
    validatablePlan: ValidatablePlan;
    studyRight: StudyRight;
    planValidationResult: PlanValidationResult;
}

export function studentModuleInfoModalOpener(): (
    modalValues: StudentModuleInfoModalValues,
) => MatDialogRef<StudentModuleInfoModalComponent, void> {
    const fudisDialogService = inject(FudisDialogService);
    return modalValues =>
        fudisDialogService.open(StudentModuleInfoModalComponent, {
            data: modalValues,
        }) as MatDialogRef<StudentModuleInfoModalComponent>;
}

interface StudentModuleInfoModalData {
    showModuleAttainmentApplicationBlock: boolean;
    showGraduationSection: boolean;
    canApplyForGraduation: boolean;
    selectedModule: DPSMBase;
    selectedModuleAttainment: ModuleAttainment | DegreeProgrammeAttainment;
    degreeProgrammeAttainmentWorkflow: DegreeProgrammeAttainmentWorkflow;
    activeModuleAttainmentWorkflow: ModuleAttainmentWorkflow;
    showCreateModuleAttainmentApplicationBlock: boolean;
    moduleVersions: DPSMBase[];
    canUseCurrentVersion: boolean;
    versionChanged: boolean;
}
@Component({
    selector: 'app-student-module-info-modal',
    templateUrl: './student-module-info-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class StudentModuleInfoModalComponent implements OnInit, OnDestroy {
    module: DPSMBase;
    validatablePlan: ValidatablePlan;
    studyRight: StudyRight;
    planValidationResult: PlanValidationResult;
    data$: Observable<Partial<StudentModuleInfoModalData>>;

    versionChangeSubject$ = new Subject<DPSMBase>();

    private readonly attainmentStudentService = inject(AttainmentStudentService);
    private readonly workflowEntityService = inject(WorkflowEntityService);
    private readonly universityService = inject(UniversityService);
    private readonly authService = inject(AuthService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly degreeProgrammeAttainmentApplicationService = inject(DegreeProgrammeAttainmentApplicationService);
    private readonly moduleEntityService = inject(ModuleEntityService);
    private readonly previewMode = inject(PreviewModeService);
    private readonly moduleInfoService = inject(ModuleInfoService);
    private readonly dialogRef = inject(MatDialogRef<StudentModuleInfoModalComponent, void>);
    private readonly values: StudentModuleInfoModalValues = inject(MAT_DIALOG_DATA);
    private readonly versionChangeWarningOpener = studentModuleInfoVersionChangeWarningOpener();

    constructor() {
        this.module = this.values.module;
        this.validatablePlan = this.values.validatablePlan;
        this.studyRight = this.values.studyRight;
        this.planValidationResult = this.values.planValidationResult;
    }

    ngOnInit() {
        const initialModule$ = of(this.module);
        const selectedModule$ = merge(initialModule$, this.versionChangeSubject$).pipe(shareReplay(1));
        const moduleAttainments$ = this.createModuleAttainmentsObservable();
        const initialModuleAttainment$ = this.createModuleAttainmentObservable(moduleAttainments$, initialModule$);
        const selectedModuleAttainment$ = this.createModuleAttainmentObservable(moduleAttainments$, selectedModule$);
        const moduleVersions$ = this.createModuleVersionsObservable(initialModule$).pipe(shareReplay(1));
        const compatibleVersionIds$ = this.createCompatibleModuleVersionIdsObservable(
            moduleVersions$,
            initialModuleAttainment$,
            moduleAttainments$,
        );
        const canUseCurrentVersion$ = this.createCanUseCurrentVersionObservable(compatibleVersionIds$, selectedModule$);
        const versionChanged$ = selectedModule$.pipe(map(selectedModule => selectedModule?.id !== this.module?.id));
        const universitySettings$ = this.createUniversitySettingsObservable();
        const showGraduationSection$ = this.createShowGraduationSectionObservable(universitySettings$, selectedModule$);
        const showCreateModuleAttainmentApplicationBlock$ = this.createShowCreateModuleAttainmentApplicationBlockObservable(
            universitySettings$,
            selectedModule$,
        );
        const workflows$ = this.createStudentWorkflowsObservable();
        const activeModuleAttainmentWorkflow$ = this.createActiveModuleAttainmentWorkflowObservable(workflows$, selectedModule$);
        const degreeProgrammeAttainmentWorkflow$ = this.createDegreeProgrammeAttainmentWorkflowObservable(workflows$, selectedModule$);
        const canApplyForGraduation$ = selectedModule$.pipe(map(module => this.canApplyForGraduation(module)));

        this.data$ = combineLatest({
            degreeProgrammeAttainmentWorkflow: degreeProgrammeAttainmentWorkflow$,
            showGraduationSection: showGraduationSection$,
            canApplyForGraduation: canApplyForGraduation$,
            selectedModule: selectedModule$,
            selectedModuleAttainment: selectedModuleAttainment$,
            activeModuleAttainmentWorkflow: activeModuleAttainmentWorkflow$,
            showCreateModuleAttainmentApplicationBlock: showCreateModuleAttainmentApplicationBlock$,
            moduleVersions: moduleVersions$,
            canUseCurrentVersion: canUseCurrentVersion$,
            versionChanged: versionChanged$,
        });
    }

    /**
     * Available versions for the module (by groupId), used to populate the version selector. Version switching is only
     * offered when the module is in the plan; otherwise only the currently viewed version is returned.
     */
    createModuleVersionsObservable(initialModule$: Observable<DPSMBase>): Observable<DPSMBase[]> {
        if (!this.validatablePlan) {
            return initialModule$.pipe(map(module => [module]));
        }
        return initialModule$.pipe(
            switchMap(module =>
                this.getAuthenticatedOrUnauthenticatedFindByGroupIdCall(module.groupId).pipe(
                    map(versions => (versions.find(version => version.id === module.id) ? versions : [...versions, module])),
                    // On fetch failure fall back to just the current version so the modal still renders (not blank).
                    this.appErrorHandler.defaultErrorHandler([module]),
                ),
            ),
        );
    }

    /**
     * Uses the authenticated call in preview mode; otherwise the unauthenticated endpoint (only published versions).
     * findByGroupId is typed as Module[], but every version is a concrete StudyModule/DegreeProgramme (DPSMBase).
     */
    getAuthenticatedOrUnauthenticatedFindByGroupIdCall(groupId: OtmId): Observable<DPSMBase[]> {
        const versions$ = this.previewMode.isPreviewMode()
            ? this.moduleEntityService.findByGroupId(groupId)
            : this.moduleEntityService.findByGroupIdUnauthenticated(groupId);
        // findByGroupId is typed Module[], but every version is a concrete StudyModule/DegreeProgramme (DPSMBase).
        return versions$.pipe(map(versions => versions as DPSMBase[]));
    }

    /**
     * Compatible version ids. If the module (as placed in the plan) is already attained, no version can be taken into
     * use, so an empty list is returned. Otherwise compatibility is resolved against the plan's curriculum period.
     */
    createCompatibleModuleVersionIdsObservable(
        moduleVersions$: Observable<DPSMBase[]>,
        initialModuleAttainment$: Observable<ModuleAttainment | DegreeProgrammeAttainment>,
        moduleAttainments$: Observable<(ModuleAttainment | DegreeProgrammeAttainment)[]>,
    ): Observable<OtmId[]> {
        return moduleVersions$.pipe(
            combineLatestWith(initialModuleAttainment$, moduleAttainments$),
            switchMap(([versions, initialAttainment, attainments]) => {
                if (initialAttainment || !this.validatablePlan) {
                    return of([]);
                }
                return this.moduleInfoService.getModuleVersionCompatibilityForPlan(
                    versions,
                    attainments,
                    this.validatablePlan.plan.curriculumPeriodId,
                );
            }),
        );
    }

    /**
     * Whether the currently selected version is among the compatible versions (i.e. can be taken into use).
     */
    createCanUseCurrentVersionObservable(
        compatibleVersionIds$: Observable<OtmId[]>,
        selectedModule$: Observable<DPSMBase>,
    ): Observable<boolean> {
        return compatibleVersionIds$.pipe(
            combineLatestWith(selectedModule$),
            map(([compatibleVersionIds, selectedModule]) => compatibleVersionIds.some(versionId => versionId === selectedModule.id)),
        );
    }

    /**
     * Opens the copy-or-replace dialog for taking the currently viewed version into use. On completion (either path)
     * the info modal closes; the copy path additionally navigates to the new plan's structure from within the dialog.
     */
    openVersionChangeWarningModal(toModule: DPSMBase): void {
        this.versionChangeWarningOpener({
            fromModule: this.module,
            toModule,
            validatablePlan: this.validatablePlan,
            studyRight: this.studyRight,
        })
            .afterClosed()
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    this.close();
                }
            });
    }

    ngOnDestroy() {
        this.versionChangeSubject$.complete();
    }

    close() {
        this.dialogRef.close();
    }

    createUniversitySettingsObservable(): Observable<UniversitySettings> {
        return this.universityService.getCurrentUniversitySettings().pipe(this.appErrorHandler.defaultErrorHandler());
    }

    canApplyForModuleAttainment(universitySettings: UniversitySettings, selectedModule: DPSMBase) {
        if (!this.validatablePlan) {
            return false;
        }
        if (!this.isStudyModule(selectedModule) || !selectedModule.graded) {
            return false;
        }
        if (!(universitySettings?.frontendFeatureToggles?.moduleAttainmentApplicationEnabled ?? true)) {
            return false;
        }
        if (!this.validatablePlan.isModuleInPlan(selectedModule.id) || this.validatablePlan.isModuleAttained(selectedModule.id)) {
            return false;
        }
        if (this.module.moduleContentApprovalRequired) {
            const moduleContentWorkflow = this.validatablePlan.getEffectiveModuleContentApproval(selectedModule.id);
            if (!moduleContentWorkflow || !_.includes(['ACCEPTED', 'CONDITIONAL'], moduleContentWorkflow.state)) {
                return false;
            }
        }
        const moduleValidationResult = _.get(this.planValidationResult, ['moduleValidationResults', selectedModule.id]);
        if (!moduleValidationResult || moduleValidationResult.state !== 'PARTS_ATTAINED') {
            return false;
        }
        return true;
    }

    createShowCreateModuleAttainmentApplicationBlockObservable(
        universitySettings$: Observable<UniversitySettings>,
        selectedModule$: Observable<DPSMBase>,
    ): Observable<boolean> {
        return universitySettings$.pipe(
            combineLatestWith(selectedModule$),
            map(([universitySettings, selectedModule]) => this.canApplyForModuleAttainment(universitySettings, selectedModule)),
        );
    }

    createActiveModuleAttainmentWorkflowObservable(
        workflows$: Observable<Workflow[]>,
        selectedModule$: Observable<Module>,
    ): Observable<ModuleAttainmentWorkflow> {
        return workflows$.pipe(
            map(workflows => workflows.filter(workflow => workflow.type === 'ModuleAttainmentWorkflow')),
            combineLatestWith(selectedModule$),
            map(([workflows, selectedModule]) =>
                (workflows as ModuleAttainmentWorkflow[]).find(
                    workflow =>
                        workflow.moduleId === selectedModule.id &&
                        [STUDENT_WORKFLOW_STATE.REQUESTED, STUDENT_WORKFLOW_STATE.IN_HANDLING].includes(
                            workflow.state as STUDENT_WORKFLOW_STATE,
                        ),
                ),
            ),
        );
    }

    createShowGraduationSectionObservable(
        universitySettings$: Observable<UniversitySettings>,
        selectedModule$: Observable<Module>,
    ): Observable<boolean> {
        return combineLatest([universitySettings$, selectedModule$]).pipe(
            map(([universitySettings, module]) => this.showGraduationSection(universitySettings, module)),
        );
    }

    showGraduationSection(universitySettings: UniversitySettings, module: Module): boolean {
        return this.isDegreeProgramme(module) && this.degreeProgrammeAttainmentApplicationEnabled(universitySettings);
    }

    isDegreeProgramme(module: Module): module is DegreeProgramme {
        return module.type === 'DegreeProgramme';
    }

    degreeProgrammeAttainmentApplicationEnabled(universitySettings: UniversitySettings): boolean {
        return universitySettings?.frontendFeatureToggles?.degreeProgrammeAttainmentApplicationEnabled ?? true;
    }

    createModuleAttainmentsObservable(): Observable<(ModuleAttainment | DegreeProgrammeAttainment)[]> {
        const validStudentAttainments$ = this.attainmentStudentService
            .getMyValidAttainments()
            .pipe(this.appErrorHandler.defaultErrorHandler());
        return validStudentAttainments$.pipe(
            shareReplay(1),
            map(
                attainments =>
                    attainments.filter(attainment => isModuleAttainment(attainment) || isDegreeProgrammeAttainment(attainment)) as (
                        | ModuleAttainment
                        | DegreeProgrammeAttainment
                    )[],
            ),
        );
    }

    createModuleAttainmentObservable(
        moduleAttainments$: Observable<(ModuleAttainment | DegreeProgrammeAttainment)[]>,
        selectedModule$: Observable<Module>,
    ): Observable<ModuleAttainment | DegreeProgrammeAttainment> {
        return moduleAttainments$.pipe(
            combineLatestWith(selectedModule$),
            map(([attainments, selectedModule]) => attainments.find(attainment => attainment.moduleId === selectedModule.id)),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    /**
     * Fetches all workflows for the current user.
     * Returns an empty array if preview mode is enabled.
     */
    createStudentWorkflowsObservable(): Observable<Workflow[]> {
        const workflowObservable$ = this.workflowEntityService.getWorkflowsByStudentId(this.authService.personId());
        return workflowObservable$.pipe(this.appErrorHandler.defaultErrorHandler(), shareReplay(1));
    }

    /**
     * Searches for DegreeProgrammeAttainmentWorkflow related to the currently selected module.
     *
     * @param workflows$ Array of workflows to search.
     * @param selectedModule$ The module that the search is performed for.
     */
    createDegreeProgrammeAttainmentWorkflowObservable(
        workflows$: Observable<Workflow[]>,
        selectedModule$: Observable<Module>,
    ): Observable<DegreeProgrammeAttainmentWorkflow> {
        return workflows$.pipe(
            map(workflows => workflows.filter(workflow => workflow.type === 'DegreeProgrammeAttainmentWorkflow')),
            combineLatestWith(selectedModule$),
            map(([workflows, selectedModule]) =>
                (workflows as DegreeProgrammeAttainmentWorkflow[]).find(
                    workflow =>
                        workflow.moduleId === selectedModule.id &&
                        [STUDENT_WORKFLOW_STATE.REQUESTED, STUDENT_WORKFLOW_STATE.IN_HANDLING].includes(
                            workflow.state as STUDENT_WORKFLOW_STATE,
                        ),
                ),
            ),
        );
    }

    isStudyModule(module: DPSMBase): module is StudyModule {
        return module.type === 'StudyModule';
    }

    canApplyForGraduation(module: Module): boolean {
        const validatablePlan = this.validatablePlan;
        return (
            !!validatablePlan &&
            this.isPlanEligibleForGraduationApplication(
                validatablePlan,
                this.studyRight,
                module,
                PlanValidationTs.getModulePlanState(
                    validatablePlan.modulesById[module.id] as Module,
                    validatablePlan,
                    this.planValidationResult,
                ),
            )
        );
    }

    private isPlanEligibleForGraduationApplication(
        validatablePlan: ValidatablePlan,
        studyRight: StudyRight,
        module: Module,
        planState: ModulePlanState | null,
    ): boolean {
        return (
            validatablePlan?.plan?.primary &&
            studyRight?.state === 'ACTIVE' &&
            module.type === 'DegreeProgramme' &&
            this.degreeProgrammeAttainmentApplicationService.isPlanStateValidForApplicationSending(planState)
        );
    }

    refreshData() {
        this.ngOnInit();
    }
}
